import { Link } from "@heroui/react";

import { EventStatusChip } from "@/src/components/EventStatusChip";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { Panel } from "@/src/pages/Dashboard/components/Panel";
import { UPCOMING_PREVIEW } from "@/src/pages/Dashboard/hooks/useEventPulse";
import { filledPlaces } from "@/src/pages/Events/eventPlaces";
import type { AdminEvent } from "@/src/pages/Events/events.type";
import { ROUTES } from "@/src/routes/paths";

type UpcomingPanelProps = {
  events: AdminEvent[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * Les prochaines parties à se jouer.
 *
 * Sans année dans la date : ces parties se tiennent dans les jours qui
 * viennent, et « 2026 » répété cinq fois n'apprendrait rien.
 *
 * ⚠️ **Le remplissage passe par `filledPlaces`**, jamais par une addition
 * écrite ici : deux calculs séparés finiraient par ne plus compter la même
 * chose, et c'est justement le remplissage qu'on vient lire.
 */
export function UpcomingPanel({
  events,
  isLoading,
  isError,
}: UpcomingPanelProps) {
  const t = useTranslations("dashboard");
  const te = useTranslations("events");
  const { locale } = useLocale();

  const when = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Panel
      emptyLabel={t("upcoming-empty")}
      errorLabel={t("panel-error")}
      href={ROUTES.events}
      isEmpty={events.length === 0}
      isError={isError}
      isLoading={isLoading}
      rows={UPCOMING_PREVIEW}
      seeAll={t("see-all")}
      seeAllLabel={t("see-all-events")}
      title={t("upcoming-title")}
    >
      {events.map((event) => (
        <li
          className="relative flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5"
          key={event.id}
        >
          {/* ⚠️ `static` : `.link` pose `position: relative`, et sans lui
              l'`after:inset-0` s'ancrerait sur le lien au lieu de la ligne. */}
          <Link
            className="static min-w-0 truncate text-sm font-medium after:absolute after:inset-0"
            href={ROUTES.event(event.id)}
          >
            {event.name}
          </Link>

          <EventStatusChip status={event.status} />

          <span className="ms-auto shrink-0 text-xs text-muted tabular-nums">
            {event.date === null
              ? te("no-date")
              : when.format(new Date(event.date))}
          </span>

          <p className="w-full text-xs text-muted tabular-nums">
            {te("places-filled", {
              filled: filledPlaces(event),
              places: event.places,
            })}
          </p>
        </li>
      ))}
    </Panel>
  );
}
